import { Dispatch, FC, Fragment, RefObject, SetStateAction, useId, useRef, useState } from "react";
import { ChevronRightIcon } from "@heroicons/react/24/outline";
import { dollars } from "./dollars";
import BottomSheet from "./BottomSheet";
import ProductDetails, { deductibleLine, detailLines, Line, plainLine } from "./ProductDetails";
import ProofUpload, { ProofFile } from "./ProofUpload";
import { hasCoverageToView, orderProducts, per, showsPrice } from "./ProtectionStep";
import ShowMore from "./ShowMore";
import { titleCase } from "./titleCase";

/** The key lines a row lists: the first few, in full (links and paragraphs kept), the deductible (which has its own place) left out. */
const LISTED = 3;
const covered = (options: any, deductible: string | null): string[] =>
  detailLines(options)
    .filter((l) => l !== deductible && plainLine(l))
    .slice(0, LISTED);
const isRich = (line: string) => line.trim().startsWith("<");

const Row: FC<{ product: any; on: boolean; onChoose: () => void; onView: () => void; bgColor?: string }> = ({
  product: p,
  on,
  onChoose,
  onView,
  bgColor,
}) => {
  const id = useId();
  const name = titleCase(p.title ?? "");
  const ded = deductibleLine(p.options);
  const facts = [ded, ...covered(p.options, ded)].filter((f): f is string => Boolean(f));
  return (
    <div className={`relative flex items-start gap-4 p-4 transition-colors ${on ? "bg-primary-6000/5" : "hover:bg-secondary-50"}`}>
      {/* The whole row is the radio: this button covers it, and View coverage sits above it. */}
      <button
        type="button"
        role="radio"
        aria-checked={on}
        aria-labelledby={`${id}-name`}
        aria-describedby={showsPrice(p) ? `${id}-facts ${id}-price` : `${id}-facts`}
        onClick={() => {
          // The page's handler toggles: pressing the chosen package again must not drop it.
          if (!on) onChoose();
        }}
        className="absolute inset-0 cursor-pointer focus-visible:outline focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-primary-6000"
      />
      <span
        aria-hidden="true"
        className={`mt-0.5 h-5 w-5 shrink-0 rounded-full bg-bg-color ${on ? "border-[5px] border-primary-6000" : "border border-text-color/40"}`}
      />
      <div className="min-w-0 flex-1">
        <span id={`${id}-name`} className="block break-words text-base font-semibold leading-tight">
          {name}
        </span>
        {facts.length > 0 && (
          <ShowMore id={`${id}-facts`} className="mt-1 break-words text-sm text-text-color/70" linkClassName="mt-1">
            {facts.map((f, i) => (
              <Fragment key={i}>
                {i > 0 && !isRich(f) && !isRich(facts[i - 1]) && " · "}
                {f === ded ? <span className="font-medium text-text-color">{f}</span> : <Line text={f} bgColor={bgColor} />}
              </Fragment>
            ))}
          </ShowMore>
        )}
        {hasCoverageToView(p) && (
          <button
            type="button"
            onClick={onView}
            aria-label={`View coverage: ${name}`}
            className="relative mt-2 inline-flex items-center gap-0.5 text-sm font-medium underline underline-offset-2"
          >
            View coverage
            <ChevronRightIcon className="h-3.5 w-3.5" aria-hidden="true" />
          </button>
        )}
      </div>
      {showsPrice(p) && (
        <span id={`${id}-price`} className="shrink-0 text-right text-lg font-semibold tabular-nums">
          {dollars(p.price)}
          <small className="text-sm font-normal text-text-color/60"> / {per(p.nature)}</small>
        </span>
      )}
    </div>
  );
};

/**
 * Step 2 of the desktop booking: protection as a stacked list of rows (1NOW-11174, replacing the cards of
 * 1NOW-11173). Each row is a radio with the name, the deductible, a few key cover lines in full and the price per
 * its unit; pressing anywhere on it chooses it. The same packages the phone lists, chosen through the page's own
 * handler (`onSelect` = attemptBonzahProductSelection), so the Bonzah terms popup, the >30-day rules and the
 * proof-of-insurance rule are the page's, not a copy. "View coverage" opens the whole ProductDetails in a
 * drawer on the right.
 */
const DesktopProtection: FC<{
  products: any[];
  loading: boolean;
  notice?: string;
  selectedValue: any;
  onSelect: (product: any) => void;
  bgColor?: string;
  proof: {
    files: ProofFile[];
    setFiles: Dispatch<SetStateAction<ProofFile[]>>;
    inputRef: RefObject<HTMLInputElement>;
  };
}> = ({ products, loading, notice, selectedValue, onSelect, bgColor, proof }) => {
  const [viewing, setViewing] = useState<any | null>(null);
  // The drawer keeps showing the package while it slides out, instead of emptying first.
  const lastViewed = useRef<any>(null);
  if (viewing) lastViewed.current = viewing;
  const shown = viewing ?? lastViewed.current;
  const list = orderProducts(products);
  const isSelected = (p: any) => JSON.stringify(selectedValue) === JSON.stringify(p?.value);
  const chosen = list.find(isSelected);

  const noticeBox = notice && (
    <p className="mb-4 rounded-xl border border-red-200 bg-red-50 p-3 text-sm text-red-700">{notice}</p>
  );
  if (list.length === 0)
    return (
      <>
        {noticeBox}
        <p className="rounded-xl border border-secondary-100 p-4 text-sm">
          {loading ? "Loading protection options…" : "No protection options are offered for this trip."}
        </p>
      </>
    );

  const close = () => setViewing(null);
  return (
    <>
      {noticeBox}
      <div
        role="radiogroup"
        aria-label="Protection"
        className="overflow-hidden rounded-xl border border-secondary-100 bg-card-color divide-y divide-secondary-100"
      >
        {list.map((p) => (
          <Row key={p.title} product={p} on={isSelected(p)} onChoose={() => onSelect(p)} onView={() => setViewing(p)} bgColor={bgColor} />
        ))}
      </div>
      {chosen?.upload_before_payment === true && (
        <div className="mt-4 rounded-xl border border-secondary-100 bg-card-color p-5">
          <h3 className="text-base font-semibold">Your own insurance</h3>
          <p className="text-sm opacity-80">Upload your proof of insurance before you continue.</p>
          <ProofUpload files={proof.files} setFiles={proof.setFiles} inputRef={proof.inputRef} />
        </div>
      )}
      <BottomSheet
        drawer
        open={viewing !== null}
        onClose={close}
        title={shown ? `${titleCase(shown.title ?? "")}: what is covered` : "Coverage"}
        footer={
          shown && (
            <div className="flex justify-end gap-3">
              <button
                type="button"
                onClick={close}
                className="min-h-[44px] rounded-lg border border-text-color/25 px-5 text-sm font-medium"
              >
                Close
              </button>
              {!isSelected(shown) && (
                <button
                  type="button"
                  onClick={() => {
                    onSelect(shown);
                    close();
                  }}
                  className="min-h-[44px] rounded-lg bg-button-color [.design-a_&]:bg-primary-6000 [.design-a_&]:text-on-primary px-5 text-sm font-medium text-on-button"
                >
                  Choose {titleCase(shown.title ?? "")}
                </button>
              )}
            </div>
          )
        }
      >
        {shown && <ProductDetails product={shown} bgColor={bgColor} />}
      </BottomSheet>
    </>
  );
};

export default DesktopProtection;
